import { readSummitFile } from "./summitService";

// platformService.js
// Fetches live data from TCC_Status.xlsx - single merged file
// Sheets: 30-Day Tracker, Blockers, Phase 1 - Foundation, Phase 2 - Integration,
//         Phase 3 - Intelligence, Phase 4 - AI Layer, CIM Readiness
// To advance phases: reorder sheets in the Excel file - first non-tracker sheet
// becomes the current phase shown in the banner.

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

// Update this sharing URL after uploading TCC_Status.xlsx to SharePoint DataMvpsSiteData
// For now using the tracker URL as placeholder - replace after upload
const STATUS_FILE_URL = "u!aHR0cHM6Ly90cmlwbGVjYy5zaGFyZXBvaW50LmNvbS86eDovcy9PcGVyYXRpb25zSVRIdWIvSVFCdFllN29udkU1UjRjTFk5SEhfTms2QVJ2emktYk1Ba3FmVmh4QlY2OVA0M00";

async function graphFetch(url, accessToken) {
  const res = await fetch(url, {
    headers: { Authorization: "Bearer " + accessToken },
  });
  if (!res.ok) {
    const err = await res.json().catch(function() { return {}; });
    throw new Error("Graph API " + res.status + ": " + (err.error?.message || res.statusText));
  }
  return res.json();
}

async function resolveSharingUrl(encodedUrl, accessToken) {
  const data = await graphFetch(
    GRAPH_BASE + "/shares/" + encodedUrl + "/driveItem",
    accessToken
  );
  return data;
}

async function getWorksheetRange(driveItem, sheetName, accessToken) {
  const encodedSheet = encodeURIComponent(sheetName);
  const url = GRAPH_BASE + "/drives/" + driveItem.parentReference.driveId +
    "/items/" + driveItem.id + "/workbook/worksheets/" + encodedSheet + "/usedRange";
  return graphFetch(url, accessToken);
}

// TCC defaults - used when no project config is provided (datamvps.tripleco.com)
const TCC_DRIVE_ID = "b!lQhaegUp90W6doNIX0gCPf75Qk46uGpGghXwC-9N3hPGlFCOtv4NT4o7JmQUKRJ-";
const TCC_FILE_ID  = "0137WX5PTNMHXORHXRHFDYOC3D2HD7ZWJ2";

async function getWorksheets(driveId, fileId, accessToken) {
  const url = GRAPH_BASE + "/drives/" + driveId + "/items/" + fileId + "/workbook/worksheets";
  const data = await graphFetch(url, accessToken);
  return data.value;
}

async function getWorksheetRangeByIds(driveId, fileId, sheetName, accessToken) {
  const encodedSheet = encodeURIComponent(sheetName);
  const url = GRAPH_BASE + "/drives/" + driveId + "/items/" + fileId + "/workbook/worksheets/" + encodedSheet + "/usedRange";
  return graphFetch(url, accessToken);
}

// Keep old name for any direct callers
async function getWorksheetRangeByUrl(encodedUrl, sheetName, accessToken) {
  return getWorksheetRangeByIds(TCC_DRIVE_ID, TCC_FILE_ID, sheetName, accessToken);
}

function parseTrackerSheet(values) {
  if (!values || values.length < 3) return [];
  const headers = values[1].map(function(h) { return String(h || "").trim().toLowerCase(); });
  const colTrack = headers.findIndex(function(h) { return h === "track"; });
  const colTask = headers.findIndex(function(h) { return h.includes("task") || h.includes("item"); });
  const colStatus = headers.findIndex(function(h) { return h === "status"; });
  const colRisk = headers.findIndex(function(h) { return h === "risk"; });
  const colOwner = headers.findIndex(function(h) { return h === "owner"; });
  const colNote = headers.findIndex(function(h) { return h === "note"; });

  const rows = [];
  for (let i = 2; i < values.length; i++) {
    const row = values[i];
    if (!row || row.every(function(c) { return !c; })) continue;
    const task = String(row[colTask] || "").trim();
    if (!task) continue;
    rows.push({
      track: String(row[colTrack] || "").trim(),
      task: task,
      status: String(row[colStatus] || "not-started").trim().toLowerCase(),
      risk: String(row[colRisk] || "FALSE").trim().toUpperCase() === "TRUE",
      owner: String(row[colOwner] || "").trim(),
      note: String(row[colNote] || "").trim(),
    });
  }
  return rows;
}

function parseBlockersSheet(values) {
  if (!values || values.length < 3) return [];
  const headers = values[1].map(function(h) { return String(h || "").trim().toLowerCase(); });
  const colBlocker = headers.findIndex(function(h) { return h === "blocker"; });
  const colDesc = headers.findIndex(function(h) { return h.includes("desc"); });
  const colOwner = headers.findIndex(function(h) { return h === "owner"; });
  const colPhase = headers.findIndex(function(h) { return h === "phase"; });
  const colDue = headers.findIndex(function(h) { return h === "due"; });

  const rows = [];
  for (let i = 2; i < values.length; i++) {
    const row = values[i];
    if (!row || row.every(function(c) { return !c; })) continue;
    const blocker = String(row[colBlocker] || "").trim();
    if (!blocker) continue;
    rows.push({
      blocker: blocker,
      description: String(row[colDesc] || "").trim(),
      owner: String(row[colOwner] || "").trim(),
      phase: String(row[colPhase] || "").trim(),
      due: String(row[colDue] || "").trim(),
      rowIndex: i + 1,
    });
  }
  return rows;
}

// Unified parser for all standard sheets (Track, Task, Status, Risk, Owner, Due, Note, Last Updated)
function parseStandardSheet(values) {
  if (!values || values.length < 3) return [];
  const headers = values[1].map(function(h) { return String(h || "").trim().toLowerCase(); });
  const col = {
    track:   headers.findIndex(function(h) { return h === "track"; }),
    task:    headers.findIndex(function(h) { return h === "task" || h === "item"; }),
    status:  headers.findIndex(function(h) { return h === "status"; }),
    risk:    headers.findIndex(function(h) { return h === "risk"; }),
    owner:   headers.findIndex(function(h) { return h === "owner"; }),
    due:     headers.findIndex(function(h) { return h === "due"; }),
    note:    headers.findIndex(function(h) { return h.includes("note") || h.includes("comment"); }),
    updated: headers.findIndex(function(h) { return h.includes("updated"); }),
  };
  const rows = [];
  for (let i = 2; i < values.length; i++) {
    const row = values[i];
    if (!row || row.every(function(c) { return !c; })) continue;
    const task = String(row[col.task > -1 ? col.task : 1] || "").trim();
    if (!task) continue;
    const rowIndex = i + 1; // convert 0-based array index to 1-based sheet row
    rows.push({
      track:   String(row[col.track > -1 ? col.track : 0]   || "").trim(),
      task:    task,
      status:  String(row[col.status > -1 ? col.status : 2]  || "not-started").trim().toLowerCase(),
      risk:    String(row[col.risk > -1 ? col.risk : 3]    || "").trim() === "TRUE",
      owner:   String(row[col.owner > -1 ? col.owner : 4]   || "").trim(),
      due:     String(row[col.due > -1 ? col.due : 5]     || "").trim(),
      note:    String(row[col.note > -1 ? col.note : 6]    || "").trim(),
      rowIndex: rowIndex, // 1-based sheet row number for PATCH
    });
  }
  return rows;
}

// ─── UNIFIED FETCH ───────────────────────────────────────────────────────────

export async function fetchStatusData(accessToken, projectConfig) {
  // Use project config IDs if provided, otherwise fall back to TCC defaults
  const driveId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.driveId)
    ? projectConfig.sharepoint.driveId
    : TCC_DRIVE_ID;
  const fileId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.statusFileId)
    ? projectConfig.sharepoint.statusFileId
    : TCC_FILE_ID;

  const worksheets = await getWorksheets(driveId, fileId, accessToken);

  let tasks = [];
  let blockers = [];
  try {
    const trackerData = await getWorksheetRangeByIds(driveId, fileId, "30-Day Tracker", accessToken);
    tasks = parseStandardSheet(trackerData.values || []);
  } catch (e) { console.warn("30-Day Tracker error:", e.message); }

  try {
    const blockersData = await getWorksheetRangeByIds(driveId, fileId, "Blockers", accessToken);
    blockers = parseBlockersSheet(blockersData.values || []);
  } catch (e) { console.warn("Blockers error:", e.message); }

  // Sheet order drives everything:
  // Sheet 1: Blockers (always first, never moves)
  // Sheet 2: Current active stage (30-Day Tracker now, Phase 1 next, etc.)
  // Sheet 3+: Remaining phases in sequence
  // To advance: move current stage sheet to the end, next sheet becomes active
  const nonBlockerSheets = worksheets.filter(function(ws) {
    var excluded = ["Blockers", "Metrics", "Source Keys", "30-Day Tracker"];
    return excluded.indexOf(ws.name) === -1;
  });

  // Sheet 2 (index 0 of nonBlockerSheets) is the current active stage
  // All non-blocker sheets become the phase accordion entries
  const phases = [];
  for (const sheet of nonBlockerSheets) {
    try {
      const data = await getWorksheetRangeByIds(driveId, fileId, sheet.name, accessToken);
      const items = parseStandardSheet(data.values || []);
      phases.push({ name: sheet.name, items: items });
    } catch (e) { console.warn("Phase sheet error:", sheet.name, e.message); }
  }

  // Current stage is the first non-blocker sheet
  const currentStage = nonBlockerSheets.length > 0 ? nonBlockerSheets[0].name : "30-Day Tracker";

  return { tasks: tasks, blockers: blockers, phases: phases, currentStage: currentStage };
}


export async function patchBlockerRow(accessToken, projectConfig, rowIndex, { description, owner, due, blockerName }) {
  const driveId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.driveId)
    ? projectConfig.sharepoint.driveId : TCC_DRIVE_ID;
  const fileId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.statusFileId)
    ? projectConfig.sharepoint.statusFileId : TCC_FILE_ID;
  if (!rowIndex && blockerName) {
    const data = await getWorksheetRangeByIds(driveId, fileId, "Blockers", accessToken);
    const vals = data.values || [];
    for (var i = 2; i < vals.length; i++) {
      if (String(vals[i][0] || "").trim() === blockerName) { rowIndex = i + 1; break; }
    }
  }
  if (!rowIndex) throw new Error("Could not determine row for blocker: " + blockerName);
  const sheetName = encodeURIComponent("Blockers");
  const base = GRAPH_BASE + "/drives/" + driveId + "/items/" + fileId + "/workbook/worksheets/" + sheetName + "/";
  async function patchCell(col, value) {
    const resp = await fetch(base + "range(address='" + col + rowIndex + "')", {
      method: "PATCH",
      headers: { "Authorization": "Bearer " + accessToken, "Content-Type": "application/json" },
      body: JSON.stringify({ values: [[value]] }),
    });
    if (!resp.ok) { const err = await resp.text(); throw new Error("PATCH " + col + rowIndex + " failed: " + err); }
  }
  // Blockers sheet: A=Blocker, B=Description, C=Owner, D=Phase, E=Due
  await Promise.all([
    patchCell("B", description || ""),
    patchCell("C", owner || ""),
    patchCell("E", due || ""),
  ]);
}


export async function patchPhaseTask(accessToken, projectConfig, sheetName, rowIndex, { status, risk, note, owner, taskName }) {
  const driveId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.driveId)
    ? projectConfig.sharepoint.driveId : TCC_DRIVE_ID;
  const fileId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.statusFileId)
    ? projectConfig.sharepoint.statusFileId : TCC_FILE_ID;
  if (!rowIndex && taskName) {
    const data = await getWorksheetRangeByIds(driveId, fileId, sheetName, accessToken);
    const vals = data.values || [];
    for (var i = 2; i < vals.length; i++) {
      if (String(vals[i][1] || "").trim() === taskName) { rowIndex = i + 1; break; }
    }
  }
  if (!rowIndex) throw new Error("Could not determine row for: " + taskName);
  const encodedSheet = encodeURIComponent(sheetName);
  const base = GRAPH_BASE + "/drives/" + driveId + "/items/" + fileId + "/workbook/worksheets/" + encodedSheet + "/";
  async function patchCell(col, value) {
    const resp = await fetch(base + "range(address='" + col + rowIndex + "')", {
      method: "PATCH",
      headers: { "Authorization": "Bearer " + accessToken, "Content-Type": "application/json" },
      body: JSON.stringify({ values: [[value]] }),
    });
    if (!resp.ok) { const err = await resp.text(); throw new Error("PATCH " + col + rowIndex + " failed: " + err); }
  }
  const patches = [
    patchCell("C", status),
    patchCell("D", risk ? "TRUE" : "FALSE"),
    patchCell("G", note || ""),
  ];
  if (owner !== undefined && owner !== null) patches.push(patchCell("E", owner));
  await Promise.all(patches);
}

// ─── PATCH TRACKER TASK ──────────────────────────────────────────────────────
// Updates status, risk, and note for a single task row in the 30-Day Tracker
// rowIndex is 1-based sheet row number stored on each task object

export async function patchTrackerTask(accessToken, projectConfig, rowIndex, { status, risk, note, taskName }) {
  const driveId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.driveId)
    ? projectConfig.sharepoint.driveId : TCC_DRIVE_ID;
  const fileId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.statusFileId)
    ? projectConfig.sharepoint.statusFileId : TCC_FILE_ID;

  if (!rowIndex && taskName) {
    const data = await getWorksheetRangeByIds(driveId, fileId, "30-Day Tracker", accessToken);
    const vals = data.values || [];
    for (var i = 2; i < vals.length; i++) {
      if (String(vals[i][1] || "").trim() === taskName) { rowIndex = i + 1; break; }
    }
  }
  if (!rowIndex) throw new Error("Could not determine row for: " + taskName);
  const sheetName = encodeURIComponent("30-Day Tracker");
  const base = GRAPH_BASE + "/drives/" + driveId + "/items/" + fileId + "/workbook/worksheets/" + sheetName + "/";

  // Status is column C (col 3), Risk is column D (col 4), Note is column G (col 7)
  const statusCol = "C";
  const riskCol   = "D";
  const noteCol   = "G";

  async function patchCell(col, value) {
    const url = base + "range(address='" + col + rowIndex + "')";
    const resp = await fetch(url, {
      method: "PATCH",
      headers: {
        "Authorization": "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ values: [[value]] }),
    });
    if (!resp.ok) {
      const err = await resp.text();
      throw new Error("PATCH " + col + rowIndex + " failed: " + err);
    }
    return resp.json();
  }

  await Promise.all([
    patchCell(statusCol, status),
    patchCell(riskCol, risk ? "TRUE" : "FALSE"),
    patchCell(noteCol, note || ""),
  ]);
}

// ─── SUMMIT CAPTURE DATA ─────────────────────────────────────────────────────
// Reads capture tab state data saved by useSummitSync
// TODO: confirm the exact SharePoint path/file pattern used by useSummitSync
// Once confirmed, implement this function to return { s3, s4, s6, s7, s9 }
// Each key should be the parsed JSON state object for that capture session

export async function fetchSummitData(accessToken, projectConfig) {
  // Reads the capture-tab JSON files directly via the now project-aware
  // summitService, keyed the same way the capture tabs save them.
  const keys = ["s3", "s4", "s6", "s7", "s9"];
  const result = {};
  await Promise.all(keys.map(async function (k) {
    try {
      const data = await readSummitFile(k, accessToken, projectConfig);
      if (data) result[k] = data;
    } catch (e) {
      console.warn("fetchSummitData: failed to read " + k + ":", e.message);
    }
  }));
  return result;
}

// ─── METRICS SHEET ───────────────────────────────────────────────────────────

function parseMetricsSheet(values) {
  if (!values || values.length < 4) return [];
  // Row 1: title, Row 2: instructions, Row 3: headers, Row 4+: data
  // Headers: Category | KPI | Target | Live Date | Source Tab | Source Notes | Status | Owner
  const headers = values[2].map(function(h) { return String(h || "").trim().toLowerCase(); });
  const col = {
    category:    headers.findIndex(function(h) { return h === "category"; }),
    kpi:         headers.findIndex(function(h) { return h === "kpi"; }),
    target:      headers.findIndex(function(h) { return h === "target"; }),
    liveDate:    headers.findIndex(function(h) { return h.includes("live") && h.includes("date"); }),
    sourceTab:   headers.findIndex(function(h) { return h.includes("source") && h.includes("tab"); }),
    sourceNotes: headers.findIndex(function(h) { return h.includes("source") && h.includes("note"); }),
    status:      headers.findIndex(function(h) { return h === "status"; }),
    owner:       headers.findIndex(function(h) { return h === "owner"; }),
    sourceKey:   headers.findIndex(function(h) { return h.includes("source") && h.includes("key"); }),
  };

  const rows = [];
  let currentCategory = "";
  for (let i = 3; i < values.length; i++) {
    const row = values[i];
    if (!row || row.every(function(c) { return !c; })) continue;
    // Category header rows have only the first cell populated (merged)
    const firstCell = String(row[0] || "").trim();
    const kpiVal = col.kpi > -1 ? String(row[col.kpi] || "").trim() : "";
    // Skip category header rows - they have a value in col 0 but not in kpi col
    if (firstCell && !kpiVal) { currentCategory = firstCell; continue; }
    if (!kpiVal) continue;
    rows.push({
      category:    currentCategory || firstCell,
      kpi:         kpiVal,
      target:      col.target > -1 ? String(row[col.target] || "").trim() : "",
      liveDate:    col.liveDate > -1 ? String(row[col.liveDate] || "").trim() : "",
      sourceTab:   col.sourceTab > -1 ? String(row[col.sourceTab] || "").trim() : "",
      sourceNotes: col.sourceNotes > -1 ? String(row[col.sourceNotes] || "").trim() : "",
      status:      col.status > -1 ? String(row[col.status] || "not-live").trim().toLowerCase() : "not-live",
      owner:       col.owner > -1 ? String(row[col.owner] || "").trim() : "",
      sourceKey:   col.sourceKey > -1 ? String(row[col.sourceKey] || "").trim() : "",
    });
  }

  // Group by category
  const grouped = [];
  rows.forEach(function(r) {
    let group = grouped.find(function(g) { return g.category === r.category; });
    if (!group) { group = { category: r.category, kpis: [] }; grouped.push(group); }
    group.kpis.push(r);
  });
  return grouped;
}

export async function fetchMetrics(accessToken, projectConfig) {
  const driveId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.driveId)
    ? projectConfig.sharepoint.driveId : TCC_DRIVE_ID;
  const fileId = (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.statusFileId)
    ? projectConfig.sharepoint.statusFileId : TCC_FILE_ID;
  try {
    const data = await getWorksheetRangeByIds(driveId, fileId, "Metrics", accessToken);
    return parseMetricsSheet(data.values || []);
  } catch (e) {
    console.warn("Metrics sheet error:", e.message);
    return [];
  }
}

// Backward compatible exports - StatusTab still calls these
// Both accept optional projectConfig for multi-project support
export async function fetchPlatformTracker(accessToken, projectConfig) {
  const data = await fetchStatusData(accessToken, projectConfig);
  return { tasks: data.tasks, blockers: data.blockers, currentStage: data.currentStage };
}

export async function fetchPhaseStatus(accessToken, projectConfig) {
  const data = await fetchStatusData(accessToken, projectConfig);
  return { phases: data.phases, currentStage: data.currentStage };
}
